import {useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import {Box, Button, Grid, InputAdornment, TextField} from '@mui/material';

import {AutoCompleteFilter} from '../../../../../components/AutoCompleteFilter/AutoCompleteFilter';
import {EmpionTooltip} from '../../../../../components/EmpionTooltip/EmpionTooltip';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {Option} from '../../../../../types/shared-types';

export interface MatchFilterOptions {
  status: Option[];
  job: Option[];
}

interface Props {
  jobOptions: Option[];
  statusOptions: Option[];
  initialJobs: Option[];
  initialStatuses: Option[];
  filterBy: (filter: MatchFilterOptions) => void;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
}

export const MatchFilters = ({
  jobOptions,
  statusOptions,
  filterBy,
  initialStatuses,
  initialJobs,
  searchTerm = '',
  onSearchTermChange,
}: Props) => {
  const {t} = useTranslation();
  const [filters, setFilters] = useState<MatchFilterOptions>({job: [], status: []});
  const [filterInstanceKey, setFilterInstanceKey] = useState(0);
  const hadListFiltersRef = useRef(false);

  useEffect(() => {
    const hasListFilters = initialJobs.length > 0 || initialStatuses.length > 0;
    if (hadListFiltersRef.current && !hasListFilters) {
      setFilterInstanceKey((k) => k + 1);
    }
    hadListFiltersRef.current = hasListFilters;
  }, [initialJobs, initialStatuses]);

  useEffect(() => {
    setFilters({
      job: [...initialJobs],
      status: [...initialStatuses],
    });
  }, [initialJobs, initialStatuses]);

  const onStatusSelect = (options: Option[]) => {
    const update = {
      ...filters,
      status: [...options],
    };
    setFilters(update);
    filterBy(update);
  };

  const onCampaignSelect = (options: Option[]) => {
    const update = {
      ...filters,
      job: [...options],
    };
    setFilters(update);
    filterBy(update);
  };

  const clearFilters = () => {
    const empty = {job: [] as Option[], status: [] as Option[]};
    setFilters(empty);
    filterBy(empty);
    onSearchTermChange?.('');
  };

  return (
    <Grid container gap='16px' flexWrap={{xs: 'wrap', lg: 'nowrap'} as const} alignItems='flex-end'>
      <AutoCompleteFilter
        key={`matches-campaign-${filterInstanceKey}`}
        sx={{width: {xs: '100%', sm: '360px', lg: '380px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
        label={String(t('matchesPage.filters.allCampaigns'))}
        options={jobOptions}
        currentOptions={initialJobs}
        onSelect={onCampaignSelect}
      />
      <AutoCompleteFilter
        key={`matches-status-${filterInstanceKey}`}
        sx={{width: {xs: '100%', sm: '210px', lg: '220px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
        label={String(t('matchesPage.filters.allStatuses'))}
        options={statusOptions}
        currentOptions={initialStatuses}
        onSelect={onStatusSelect}
      />
      {onSearchTermChange && (
        <TextField
          size='small'
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          label={String(t('matchesPage.boardSearch.label'))}
          placeholder={String(t('matchesPage.boardSearch.placeholder'))}
          sx={{width: {xs: '100%', sm: '240px', lg: '250px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      )}
      <Box sx={{width: {xs: '100%', sm: 'auto'}, display: 'flex', alignItems: 'flex-end', flexShrink: 0}}>
        <EmpionTooltip title={String(t('matchesPage.filters.clear'))} isEnabled placement='top'>
          <Button
            type='button'
            variant='outlined'
            onClick={clearFilters}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: '5px',
              textTransform: 'none',
              borderColor: greenColors[700],
              color: 'text.primary',
            }}
          >
            <RestartAltIcon fontSize='small' />
          </Button>
        </EmpionTooltip>
      </Box>
    </Grid>
  );
};
